<script lang="ts" setup>
import { computed, ref, PropType, watch } from "vue";
import { getStorageProxyUrl, Formatter, MimeType } from "@/util";
import {
  DocumentTags,
  Tooltip,
  LoadingSpinner,
  EditableInput,
} from "@/components";
import { useStore } from "vuex";
import {
  type WorkspaceDocumentWithLinkingPagesFragment,
  type DocumentUsageCountsRecord,
  useVeevaDocumentMetadataQuery,
} from "@/graphql/operations";
import { useRouter } from "vue-router";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { useI18n } from "vue-i18n";

import { enUS, de, nl, fr } from "date-fns/locale";
import type { Locale } from "date-fns";

const { locale, t } = useI18n();

const dfnsLocale = computed<Locale>(() => {
  const map: Record<string, Locale> = { en: enUS, de, nl, fr };
  return map[locale.value] ?? enUS;
});

const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);
const showUnbrandedClassification = computed(() =>
  store.getters.user.hasUnbrandedDocumentClassification(),
);
const router = useRouter();

const props = defineProps({
  document: {
    type: Object as PropType<WorkspaceDocumentWithLinkingPagesFragment>,
    required: true,
  },
  documentUsage: {
    type: Object as PropType<DocumentUsageCountsRecord>,
    required: true,
  },
  mayAdministrate: {
    type: Boolean,
    required: true,
  },
  savingUserDownload: {
    type: Boolean,
    default: false,
  },
  isProcessing: {
    type: Boolean,
    required: true,
  },
});

const { data, executeQuery } = useVeevaDocumentMetadataQuery({
  variables: {
    documentId: props.document.id,
  },
  requestPolicy: "cache-and-network",
});

watch(
  () => props.isProcessing,
  (val, oldVal) => {
    if (val !== oldVal) {
      console.log("execute query");
      executeQuery();
    }
  },
  { immediate: false },
);

const veevaMetadata = computed(() =>
  data.value?.veevaDocumentMetadata?.nodes &&
  data.value?.veevaDocumentMetadata?.nodes.length > 0
    ? data.value?.veevaDocumentMetadata?.nodes[0]
    : undefined,
);

const lastUpdatedAt = computed(() =>
  Formatter.formatDate(new Date(props.document.displayDate), "%Y-%m-%d %H:%M"),
);

const isVideo = computed(() => MimeType.isVideo(props.document.mimeType));

const tags = computed(() =>
  props.document && props.document.tags
    ? (props.document.tags as Array<string>)
    : [],
);

const type = computed(() => {
  if (props.document.originalMimeType) {
    return MimeType.getName(props.document.originalMimeType);
  }

  return MimeType.getName(props.document.mimeType);
});

const size = computed(() =>
  Formatter.formatSize(parseInt(props.document.fileSize, 10)),
);
const duration = computed(() => Formatter.formatDuration(props.document.pages));

const emit = defineEmits([
  "updateTags",
  "updateShareable",
  "updateDownloadable",
  "updateUserDownload",
  "updateShareable",
  "updateSupportsFlows",
  "updateUnbranded",
  "updateExternalId",
  "updateDisplayTeaserText",
  "updateExpirationDate",
]);
const emitUpdateTags = (tags: Array<string>) => {
  emit("updateTags", tags);
};

const shareable = ref<boolean>(props.document.doesSupportSharedspace);
const updateShareable = () => {
  emit("updateShareable", shareable.value);
};

const isDownloadable = ref<boolean>(props.document.isDownloadable);
const updateIsDownloadable = () => {
  emit("updateDownloadable", isDownloadable.value);
};

const allowUserDownload = ref(props.document.allowUserDownload);
watch(
  [() => props.document.allowUserDownload, () => props.savingUserDownload],
  () => {
    if (!props.savingUserDownload) {
      allowUserDownload.value = props.document.allowUserDownload;
    }
  },
);
const updateUserDownload = () => {
  if (props.mayAdministrate && !props.savingUserDownload) {
    emit("updateUserDownload", allowUserDownload.value);
  }
};

const supportsFlows = ref<boolean>(props.document.doesSupportFlows);
const updateSupportsFlows = () => {
  emit("updateSupportsFlows", supportsFlows.value);
};

const isUnbranded = ref<boolean>(props.document.isUnbranded);
watch(
  () => props.document.isUnbranded,
  (newValue) => {
    isUnbranded.value = newValue;
  },
);
const updateUnbranded = () => {
  emit("updateUnbranded", isUnbranded.value);
};

const displayTeaserTextInSpace = ref<boolean>(
  props.document.displayTeaserTextInSpace,
);
const updateDisplayTeaserText = () => {
  emit("updateDisplayTeaserText", displayTeaserTextInSpace.value);
};

const externalId = ref<string | undefined>(
  props.document.externalId || undefined,
);

const editingExternalId = ref(false);

// Watch for external changes to props.document.externalId and update local value
watch(
  () => props.document.externalId,
  (newValue) => {
    externalId.value = newValue || undefined;
  },
);

const handleExternalIdSubmit = (newValue: string) => {
  const oldValue = externalId.value;
  externalId.value = newValue;
  editingExternalId.value = false;

  // Only show flash message if value actually changed
  if (oldValue !== newValue) {
    emit("updateExternalId", newValue);
  }
};

const handleExternalIdCancel = () => {
  editingExternalId.value = false;
};

const startEditingExternalId = () => {
  editingExternalId.value = true;
};

const expirationDate = ref<string | undefined>(
  props.document.expirationDate || undefined,
);

const editingExpirationDate = ref(false);

// Watch for external changes to props.document.expirationDate and update local value
watch(
  () => props.document.expirationDate,
  (newValue) => {
    expirationDate.value = newValue || undefined;
  },
);

const handleExpirationDateSubmit = (newValue: string) => {
  emit("updateExpirationDate", newValue || null);
};

const linkings = computed(() => props.document.linkingPages?.nodes);
</script>

<template>
  <div>
    <div v-if="isProcessing">
      <div>
        <LoadingSpinner :color="'var(--classic-text, black)'" />
      </div>
      Processing...
    </div>

    <template v-else>
      <div class="row">
        <label>{{ $t("pvDocEditor.info.updatedAt.label") }}:</label>
        <span>{{ lastUpdatedAt }}</span>
      </div>
      <div class="row">
        <template v-if="isVideo">
          <label>{{ $t("pvDocEditor.info.duration.label") }}:</label>
          <span>{{ duration }}</span>
        </template>
        <template v-else>
          <label>{{ $t("pvDocEditor.info.pages.label") }}:</label>
          <span>{{ document.pages }}</span>
        </template>
      </div>
      <div class="row">
        <label>{{ $t("pvDocEditor.info.type.label") }}:</label>
        <span>{{ type }}</span>
      </div>
      <div class="row">
        <label>{{ $t("pvDocEditor.info.size.label") }}:</label>
        <span>{{ size }}</span>
      </div>
      <div class="row" v-if="document.importSource">
        <label>{{ $t("pvDocEditor.info.importSource.label") }}:</label>
        <span
          class="import_badge"
          :class="`import_badge_${document.importSource}`"
        >
          <span
            class="material-symbols-outlined"
            v-if="document.importSource === 'local'"
            >upload_file</span
          >
          <span
            class="material-symbols-outlined"
            v-else-if="document.importSource === 'url'"
            >link</span
          >
          <span
            class="material-symbols-outlined"
            v-else-if="document.importSource === 'promomats_api'"
            >cloud_download</span
          >
          {{ $t(`pvDocEditor.info.importSource.${document.importSource}`) }}
        </span>
      </div>
    </template>

    <div class="row separated">
      <label>{{ $t("pvDocTags.docTags.label") }}</label>
      <DocumentTags :tags="tags" @change="emitUpdateTags" />
    </div>

    <div class="row separated"></div>

    <div class="row" v-if="showUnbrandedClassification">
      <div class="checkbox_container">
        <input
          id="classic-document-unbranded"
          type="checkbox"
          v-model="isUnbranded"
          @change="updateUnbranded"
        />
      </div>
      <label for="classic-document-unbranded">{{
        $t("pvDocEditor.info.unbranded.label")
      }}</label>
    </div>

    <div class="row" v-if="store.getters.user.hasCustomFlows()">
      <div class="checkbox_container">
        <input
          type="checkbox"
          v-model="supportsFlows"
          @change="updateSupportsFlows"
        />
      </div>
      <label>{{ $t("pvDocEditor.info.supportsFlows.label") }}</label>
    </div>

    <div class="row user_download_option">
      <div class="checkbox_container">
        <input
          id="classic-document-user-download"
          type="checkbox"
          v-model="allowUserDownload"
          :disabled="!mayAdministrate || savingUserDownload"
          @change="updateUserDownload"
        />
      </div>
      <label for="classic-document-user-download">{{
        $t("pvDocEditor.info.userDownload.label")
      }}</label>
      <Tooltip
        class="right multiline user_download_help"
        keyboard-accessible
        :aria-label="$t('pvDocEditor.info.userDownload.label')"
      >
        <template v-slot>
          <span class="material-symbols-outlined" aria-hidden="true">info</span>
        </template>
        <template #tooltip>{{
          $t("pvDocEditor.info.userDownload.help")
        }}</template>
      </Tooltip>
    </div>

    <template v-if="canUseSharedSpaces">
      <div class="row">
        <div class="checkbox_container">
          <input
            type="checkbox"
            v-model="shareable"
            @change="updateShareable"
          />
        </div>
        <label>{{ $t("pvDocEditor.info.shareable.label") }}</label>
      </div>

      <div class="row" v-if="shareable">
        <div class="checkbox_container">
          <input
            type="checkbox"
            v-model="isDownloadable"
            @change="updateIsDownloadable"
          />
        </div>
        <label>{{ $t("pvDocEditor.info.isDownloadable.label") }}</label>
      </div>

      <div v-if="isVideo" class="row">
        <div class="checkbox_container">
          <input
            type="checkbox"
            v-model="displayTeaserTextInSpace"
            @change="updateDisplayTeaserText"
          />
        </div>
        <label>{{
          $t("pvDocEditor.info.displayTeaserTextInSpace.label")
        }}</label>
      </div>
    </template>

    <template v-if="veevaMetadata">
      <div class="row separated">
        <label>
          {{ $t("pvDocEditor.veevaIntegration.label") }}
          <tooltip class="right">
            <template v-slot>
              <span class="material-symbols-outlined"> info </span>
            </template>
            <template #tooltip>
              {{ $t("pvDocEditor.externalId.info") }}
            </template>
          </tooltip>
        </label>
      </div>
      <div class="row">
        <label>
          {{ $t("pvDocEditor.veevaIntegration.documentNumber") }}
        </label>
        <span>{{ veevaMetadata.documentNumber }}</span>
      </div>
      <div class="row">
        <label>
          {{ $t("pvDocEditor.veevaIntegration.documentId") }}
        </label>
        <span>{{ veevaMetadata.veevaDocumentId }}</span>
      </div>
      <div class="row">
        <label>
          {{ $t("pvDocEditor.veevaIntegration.expirationDate") }}
        </label>
        <span>{{ veevaMetadata.expirationDate }}</span>
      </div>
      <div class="row">
        <label>
          {{ $t("pvDocEditor.veevaIntegration.version") }}
        </label>
        <span>{{ veevaMetadata.majorVersion }}</span
        >.<span>{{ veevaMetadata.minorVersion }}</span>
      </div>
    </template>

    <template v-else>
      <div class="row separated">
        <label>
          {{ $t("pvDocEditor.externalId.label") }}
          <tooltip class="right">
            <template v-slot>
              <span class="material-symbols-outlined"> info </span>
            </template>
            <template #tooltip>
              {{ $t("pvDocEditor.externalId.info") }}
            </template>
          </tooltip>
        </label>
      </div>

      <div class="row">
        <EditableInput
          v-if="editingExternalId"
          :value="externalId || ''"
          @submit="handleExternalIdSubmit"
          @cancel="handleExternalIdCancel"
        />
        <div
          v-else-if="document.importSource === 'url'"
          class="external_id_readonly"
        >
          <span class="external_id_text">{{
            externalId || $t("pvDocEditor.externalId.placeholder")
          }}</span>
        </div>
        <div
          v-else
          class="external_id_container"
          @click="startEditingExternalId"
        >
          <span class="external_id_text">{{
            externalId || $t("pvDocEditor.externalId.placeholder")
          }}</span>
          <span class="material-symbols-outlined edit_icon"> edit </span>
        </div>
      </div>
    </template>

    <template v-if="document.importSource === 'local'">
      <div class="row separated">
        <label>
          {{ $t("pvDocEditor.expirationDate.label") }}
          <tooltip class="right">
            <template v-slot>
              <span class="material-symbols-outlined"> info </span>
            </template>
            <template #tooltip>
              {{ $t("pvDocEditor.expirationDate.info") }}
            </template>
          </tooltip>
        </label>
      </div>

      <div class="row">
        <VueDatePicker
          v-model="expirationDate"
          :time-config="{ enableTimePicker: false }"
          :auto-apply="false"
          :min-date="new Date()"
          :locale="dfnsLocale"
          :formats="{ input: 'P' }"
          :action-row="{
            selectBtnLabel: t('submit'),
            cancelBtnLabel: t('cancel'),
          }"
          @update:model-value="handleExpirationDateSubmit"
        ></VueDatePicker>
      </div>
    </template>

    <div class="row separated" v-if="linkings && linkings.length > 0">
      <label>{{ $t("pvDocEditor.info.linkings.label") }}:</label>
      <div v-for="linking in linkings" :key="linking!.id" class="doc_row">
        <template v-if="linking?.document">
          <div class="thumbnail_container">
            <img
              :src="getStorageProxyUrl(linking.document.thumbnailDownloadUrl)"
              :alt="linking.document.displayTitle"
              class="thumbnail_image"
            />
          </div>
          <div class="doc_title">
            <div>
              <a
                v-if="linking.document.workspace?.id"
                :href="
                  router.resolve({
                    name: 'StackedProjector',
                    params: { workspaceId: linking.document.workspace.id },
                    query: {
                      fid: linking.document.firestoreId,
                    },
                  })?.href
                "
                target="_blank"
                class="link"
              >
                <span>
                  {{ linking.document.displayTitle }}
                </span>
                <span class="material-symbols-outlined"> open_in_new </span>
              </a>
              <span v-else>
                {{ linking.document.displayTitle }}
              </span>
            </div>
            <div class="info">
              <span>
                {{ $t("pvDocEditor.info.linkings.linkingPage") }}:
                {{ linking.pageNumber }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <div
        v-if="
          documentUsage.numPresentations &&
          documentUsage.numPresentations > linkings.length
        "
        class="usage_row"
      >
        <span class="material-symbols-outlined"> info </span>
        {{
          $t("pvDocEditor.info.linkings.withNoAccess", {
            count: documentUsage.numPresentations - linkings.length,
          })
        }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
div {
  font-size: 0.9rem;
}

.row {
  margin-bottom: 0.4em;
}

.row.separated {
  border-top: 1px solid var(--classic-border-strong, #cecece);
  padding-top: 1em;
  margin-top: 1.3em;
}

label {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  width: 150px;
  display: inline-block;
}

.checkbox_container + label {
  width: auto;
}

.user_download_option {
  display: flex;
  align-items: flex-start;
  gap: 0.3em;
}

.user_download_option .checkbox_container {
  flex: 0 0 auto;
}

.user_download_option label {
  flex: 1;
  min-width: 0;
}

.user_download_help {
  flex: 0 0 auto;
}

.user_download_help .material-symbols-outlined {
  font-size: 1.2em;
}

.user_download_option .user_download_help.multiline :deep(.tooltip) {
  width: 280px;
  max-width: calc(100vw - 32px);
  overflow-wrap: anywhere;
}

a.link {
  color: inherit;
  text-decoration: none;
  line-height: 1.6;
}

a.link span {
  text-decoration: underline;
}

a.link .material-symbols-outlined {
  font-size: 1.2em;
  vertical-align: middle;
  text-decoration: none;
  margin-left: 0.2em;
}

.usage_row,
.doc_row {
  display: flex;
  align-items: center;
  margin-top: 0.3em;
  width: 100%;
}

.usage_row {
  margin-top: 1em;
}

.usage_row .material-symbols-outlined {
  margin-right: 0.3em;
}

.doc_row .thumbnail_container {
  height: 60px;
  width: 60px;
  margin-right: 0.5em;
}

.thumbnail_image {
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
  height: 100%;
  width: 100%;
}

.doc_row .info {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
}

.doc_row .doc_title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 60px);
}

label .material-symbols-outlined {
  font-size: 1.2em;
  vertical-align: -3px;
}

.external_id_container {
  display: flex;
  align-items: center;
  cursor: pointer;
  width: fit-content;
}

.external_id_readonly {
  display: flex;
  align-items: center;
  width: fit-content;
}

.external_id_text {
  display: inline;
}

.edit_icon {
  font-size: 1rem;
  padding: 0.5em;
  opacity: 0.5;
}

.import_badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  background: var(--classic-selection, #e3f2fd);
  color: var(--classic-focus, #1976d2);
  padding: 0.2em 0.6em;
  border-radius: 4px;
  font-size: 0.85em;
}

.import_badge .material-symbols-outlined {
  font-size: 1.1em;
}
</style>
